import {CommentsCategory} from '@domain/comments'
import {useScrollDrag} from '@domain/shared'
import {useMediaQueryBreakpoints} from '@hconnect/common/hooks'
import {moveElementsHorizontally} from '@hconnect/ganttchart/src/helpers/chart.helpers'
import {useGanttChartCells} from '@hconnect/ganttchart/src/hooks/useGanttChartCells'
import {dataTestId} from '@hconnect/uikit'
import {Box, Stack, useTheme} from '@mui/material'
import {useQueryErrorResetBoundary} from '@tanstack/react-query'
import {useDateScale} from '@widgets/common-d3-chart'
import {SelectedAssetCommentProvider, useCommentsPermissions} from '@widgets/planning-comments'
import {usePlanningChartStartEnd} from '@widgets/planning-schedule-chart'
import React, {useCallback, useLayoutEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {OperatorViewErrorBoundary} from '../components/OperatorViewErrorBoundary'
import {OperatorViewSlider} from '../components/OperatorViewSlider'
import {FEATURE_NAMESPACE} from '../consts'
import {PlannerFeature, usePlannerFeature, useElementWidth} from '../hooks'
import {calculateRangeWithinBoundary} from '../utils'

import {OperatorChartCard} from './OperatorChartCard'
import {OperatorElectricityChartCard} from './OperatorElectricityChartCard'

interface OperatorViewChartContainerProps {
  plantCode: string
}

const _OperatorViewChartContainer: React.FC<OperatorViewChartContainerProps> = ({plantCode}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {spacing} = useTheme()
  const {sm: isMobile} = useMediaQueryBreakpoints()
  const {startOfPlan, endOfPlan, hoursRange, setHoursRange, hoursList, stepsPerHour, totalSteps} =
    usePlanningChartStartEnd()
  const areCommentsEnabled = usePlannerFeature(PlannerFeature.PlanningComments)
  const shouldShowExactElectricityPrices = usePlannerFeature(
    PlannerFeature.OperatorViewExactElectricityPrices
  )
  const shouldShowColorCodedElectricityPrices = usePlannerFeature(
    PlannerFeature.OperatorViewColorCodedElectricityPrices
  )
  const {canViewComments: canViewScheduleItemComments} = useCommentsPermissions(
    CommentsCategory.AssetOperationTimes
  )
  const {canViewComments: canViewAssetComments} = useCommentsPermissions(CommentsCategory.Assets)
  const shouldShowScheduleItemComments = areCommentsEnabled && canViewScheduleItemComments
  const shouldShowAssetComments = areCommentsEnabled && canViewAssetComments

  const [planningLayoutWrapperRef, planningLayoutWidth] = useElementWidth()
  const cardXPadding = parseInt(spacing(isMobile ? 2 : 3))
  const availableWidth = planningLayoutWidth - 2 * cardXPadding
  const labelColumnWidth = isMobile ? 100 : 120
  const dateLabelHeight = 24
  const hourlyPriceHeight = 30
  const cellHeight = 50
  const chartWidth = availableWidth === 0 ? 0 : availableWidth - labelColumnWidth

  const {updateGanttCells, cellWidth, cellsDisplayed} = useGanttChartCells({
    hoursDisplayed: hoursRange[1] - hoursRange[0],
    cellsPerHour: stepsPerHour,
    chartWidth
  })

  const ganttChartRef = useRef<HTMLDivElement>(null)
  const dragScrollWrapperRef = useRef<HTMLDivElement>(null)

  const [immediateHourOffset] = hoursRange

  useLayoutEffect(() => {
    moveElementsHorizontally(cellWidth * immediateHourOffset, [ganttChartRef])
  }, [cellWidth, immediateHourOffset])

  const xScale = useDateScale({
    domain: [startOfPlan, endOfPlan],
    range: [0, cellWidth * totalSteps]
  })

  const updateHourRangeOnScrollOrDrag = useCallback(
    (delta: number) => {
      const currentOffset = immediateHourOffset + delta
      const newRange = calculateRangeWithinBoundary(
        [currentOffset, currentOffset + cellsDisplayed],
        [0, totalSteps]
      )
      setHoursRange(newRange)
    },
    [immediateHourOffset, cellsDisplayed, setHoursRange, totalSteps]
  )

  useScrollDrag({
    ref: dragScrollWrapperRef,
    callback: updateHourRangeOnScrollOrDrag,
    stepWidth: cellWidth,
    isDragEnabled: isMobile
  })

  const {reset} = useQueryErrorResetBoundary()

  return (
    <Box {...dataTestId('operator_view_chart_container')} ref={planningLayoutWrapperRef}>
      <Stack spacing={2}>
        <OperatorViewSlider updateGanttCells={updateGanttCells} cellWidth={cellWidth} />
        <Stack spacing={2} ref={dragScrollWrapperRef} {...dataTestId('chart_scroll_drag_wrapper')}>
          <OperatorViewErrorBoundary
            title={t('error.operatorChartCardBoundaryError')}
            onRetry={reset}
            fallbackTestId="operator_chart_card_fallback"
          >
            <SelectedAssetCommentProvider>
              <OperatorChartCard
                plantCode={plantCode}
                startOfPlan={startOfPlan}
                endOfPlan={endOfPlan}
                labelColumnWidth={labelColumnWidth}
                dateLabelHeight={dateLabelHeight}
                hourlyPriceHeight={hourlyPriceHeight}
                cellHeight={cellHeight}
                cellWidth={cellWidth}
                hoursDisplayed={cellsDisplayed}
                xScale={xScale}
                hoursList={hoursList}
                ref={ganttChartRef}
                shouldShowExactPrices={shouldShowExactElectricityPrices}
                shouldShowColorCodedElectricityPrices={shouldShowColorCodedElectricityPrices}
                shouldShowScheduleItemComments={shouldShowScheduleItemComments}
                shouldShowAssetComments={shouldShowAssetComments}
              />
            </SelectedAssetCommentProvider>
          </OperatorViewErrorBoundary>
          <OperatorViewErrorBoundary
            title={t('error.electricityChartCardBoundaryError')}
            onRetry={reset}
            fallbackTestId="electricity_chart_card_fallback"
          >
            <OperatorElectricityChartCard
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              hoursList={hoursList}
              cellWidth={cellWidth}
              labelColumnWidth={labelColumnWidth}
              cellsDisplayed={cellsDisplayed}
            />
          </OperatorViewErrorBoundary>
        </Stack>
      </Stack>
    </Box>
  )
}

export const OperatorViewChartContainer = React.memo(_OperatorViewChartContainer)
